import { normalizeGroup, type MeterUploadRowData } from "./buildTrialRunRow";

// A raw extraction-point ("offtake") record from GET /admin/extractionPoints
// (scheme-scoped via the level1ResourceId query param). The endpoint returns the full
// model with `subscriber` and `meter` preloaded — only the fields the offtake cell
// needs are typed here. `meter` is the offtake's active meter (the controller preloads
// only the active one), so its serial number is the one the trial run validates against.
export type ExtractionPointLookupRecord = {
  id: string;
  name: string;
  isActive?: boolean;
  // SEQWATER-only properties of the extraction point.
  group?: string | null;
  sequence?: number | null;
  meter?: { serialNo: string } | null;
  subscriber?: { accountNumber: string } | null;
};

// An option for the offtake-cell select. `value` is the offtake NAME (the field the row
// stores as extractionPointName), so a selection maps straight onto the row and there is
// no id-to-name indirection. serialNo/group/sequence ride along so the cascade can fill
// the row's meter / group / sequence without a second lookup.
export type OfftakeOption = {
  label: string;
  value: string;
  name: string;
  serialNo: string;
  group: string;
  sequence: number;
};

const toOption = (ep: ExtractionPointLookupRecord): OfftakeOption => {
  const name = ep.name ?? "";
  return {
    label: name,
    value: name,
    name,
    serialNo: ep.meter?.serialNo ?? "",
    group: ep.group ?? "",
    // The API serialises the decimal(10,3) `sequence` column as a string ("8.000").
    // Coerce it to a real number here — the value cascades onto the row's numeric `seq`,
    // and the trial run compares it (number) against the server's numeric seq; a string
    // would make `8 !== "8.000"` a permanent false "sequence differs" warning.
    sequence: ep.sequence != null ? Number(ep.sequence) : 0,
  };
};

// Map a scheme's extraction points to offtake options FILTERED to the chosen subscriber
// (by account number — the field the row stores), skipping records without a name (the
// row keys on the offtake name) and de-duplicating by name (keep the first). An empty
// account number yields no options (US-11: a subscriber must be chosen first to scope
// the offtake list). Tolerates an undefined list while the lookup loads.
export const toOfftakeOptions = (
  extractionPoints: ExtractionPointLookupRecord[] | undefined,
  accountNumber: string | undefined,
): OfftakeOption[] => {
  if (!accountNumber) return [];
  const seen = new Set<string>();
  const options: OfftakeOption[] = [];
  for (const ep of extractionPoints ?? []) {
    if (ep?.subscriber?.accountNumber !== accountNumber) continue;
    const name = ep.name ?? "";
    if (!name || seen.has(name)) continue;
    seen.add(name);
    options.push(toOption(ep));
  }
  return options;
};

// The option representing the row's current offtake. A parsed/draft name with no
// matching offtake still renders as the current value — a synthetic option carrying the
// raw name — so it shows and can be replaced; an empty value selects nothing.
export const resolveCurrentOption = (
  extractionPointName: string | undefined,
  options: OfftakeOption[],
): OfftakeOption | null => {
  if (!extractionPointName) return null;
  const match = options.find(o => o.value === extractionPointName);
  if (match) return match;
  return {
    label: extractionPointName,
    value: extractionPointName,
    name: extractionPointName,
    serialNo: "",
    group: "",
    sequence: 0,
  };
};

// Pure cascade (US-15): map a looked-up offtake to the row fields it derives. A
// non-SEQWATER scheme derives only the meter serial; SEQWATER also derives the group
// and sequence, which are properties of the extraction point.
export const deriveRowFromExtractionPoint = (
  option: OfftakeOption,
  isSeqwater: boolean,
): { serialNo: string; group?: string; seq?: number } =>
  isSeqwater
    ? { serialNo: option.serialNo, group: option.group, seq: option.sequence }
    : { serialNo: option.serialNo };

// Apply an offtake selection to the row: set the offtake name and the derived fields
// (the cascade overwrites them — the cell asks first when that would replace a different
// existing value, see cascadeWouldOverwrite). Clearing the select empties only the
// offtake name; the derived fields are left as-is and the trial run flags the gap.
export const applyOfftakeSelection = (
  rowData: MeterUploadRowData,
  option: OfftakeOption | null,
  isSeqwater: boolean,
): MeterUploadRowData =>
  option
    ? {
        ...rowData,
        extractionPointName: option.name,
        ...deriveRowFromExtractionPoint(option, isSeqwater),
      }
    : { ...rowData, extractionPointName: "" };

// Would applying this selection REPLACE a currently non-empty cell with a different
// value? Drives the "overwrite, but confirm first" UX: empty cells (or values that
// already match the offtake) fill silently; a real overwrite asks first. A non-SEQWATER
// scheme only ever cascades the serial, so group/sequence differences are ignored there.
export const cascadeWouldOverwrite = (
  rowData: MeterUploadRowData,
  option: OfftakeOption,
  isSeqwater: boolean,
): boolean => {
  const serialClobbered =
    (rowData.serialNo ?? "") !== "" && (rowData.serialNo ?? "") !== option.serialNo;
  if (!isSeqwater) return serialClobbered;

  // Match group/seq the same way the trial run does (group case-insensitive + trimmed,
  // seq numeric) so a value that already matches the offtake — give or take a trailing
  // space, letter case, or decimal representation — fills silently instead of prompting
  // to overwrite a "different" value that is really the same.
  const fileGroup = normalizeGroup(rowData.group ?? "");
  const groupClobbered =
    fileGroup !== "" && fileGroup !== normalizeGroup(option.group);
  const seqClobbered =
    rowData.seq != null && Number(rowData.seq) !== Number(option.sequence);
  return serialClobbered || groupClobbered || seqClobbered;
};
